import { Injectable } from '@angular/core';

import { AzavistaApiService, ICancellationTerm, IDependency, IProduct, ITax, ProductModuleType, ProductType} from '@azavista/servicelib';
import {
    IAdvancedSearchData, IAzavistaGroupedFieldsComponentData, IField, IRowAction, IAzavistaSelectedItemsCountData, NotificationsService, NotificationMessageType,
    getAzavistaMemoryCache} from '@azavista/components/shared';
import { IColumnsListSetting, IconName, RequiredScopesService, SettingEntityType, SettingsService, SharedService, TRANSLATION_MAP, TranslationKey } from '../../shared';
import { IConfirmationDialogComponentData } from '../../shared/confirmation-dialog/confirmation-dialog.interfaces';
import { ProductsRowActionId } from './enums';

export const isProductValid = (product: IProduct, notificationsSvc?: NotificationsService): boolean => {
    const { min_per_buyer = 0, max_per_buyer } = product;
    const isMinPerBuyerInvalid = max_per_buyer != null && min_per_buyer > max_per_buyer;
    if (isMinPerBuyerInvalid && notificationsSvc) {
        const message = `min_per_buyer of '${min_per_buyer}' should not be greater than max_per_buyer of '${max_per_buyer}'`;
        notificationsSvc.showMessage(NotificationMessageType.error, message);
    }
    return !isMinPerBuyerInvalid;
};

export type GetProductColumnsOptions = {
    dependencies?: IDependency[];
    timezone?: string;
    taxes: ITax[];
    cancellationTerms: ICancellationTerm[];
    module: ProductModuleType,
}

@Injectable({
    providedIn: 'root'
})
export class ProductsService {
    private readonly translatedProductTypeTranslationKeyPrefix = 'PRODUCT_TYPE_';

    sharedData = this.getSharedData();

    constructor(
        private sharedSvc: SharedService, private rsSvc: RequiredScopesService, private settingsSvc: SettingsService,
        private apiSvc: AzavistaApiService
    ) { }

    getProductTypeTranslation(productType: string): string {
        const translationKey = (this.translatedProductTypeTranslationKeyPrefix + productType.toUpperCase()) as TranslationKey;
        return this.sharedSvc.translate(translationKey);
    }

    getProductsColumns(options: GetProductColumnsOptions): IField[] {
        const result: IField[] = [];
        const category = this.sharedSvc.translate(TranslationKey.productFields);
        const {dependencies, timezone, cancellationTerms, taxes, module } = options ?? {};
        result.push({
            id: 'name', name: 'name', label: this.sharedSvc.translate(TranslationKey.name), category: category,
            schema: { type: 'string' }, editable: true, builtin: true, visible_for_planners: true,
            mandatory_for_planners: true,
        });
        result.push(this.sharedSvc.createSelectField({
            label: this.sharedSvc.translate(TRANSLATION_MAP.MODULE),
            name: 'module', id: 'module', category, editable: false
        }, {
            [ProductModuleType.global]: TRANSLATION_MAP.GLOBAL,
            [ProductModuleType.event]: TRANSLATION_MAP.EVENT
        }));
        result.push({
            id: 'category', name: 'category', label: this.sharedSvc.translate(TranslationKey.category), category: category,
            schema: { type: 'string' }, editable: true, builtin: true, visible_for_planners: true
        });
        result.push(this.sharedSvc.createSelectField<ProductType>({
            label: this.sharedSvc.translate(TRANSLATION_MAP.TYPE),
            id: 'type', name: 'type', category, editable: true, builtin: true, visible_for_planners: true,
            mandatory_for_planners: true,
        }, {
            ticket: TRANSLATION_MAP.PRODUCT_TYPE_TICKET,
            custom: TRANSLATION_MAP.PRODUCT_TYPE_CUSTOM,
            session: TRANSLATION_MAP.PRODUCT_TYPE_SESSION,
            donation: TRANSLATION_MAP.PRODUCT_TYPE_DONATION,
        }));
        result.push({
            id: 'price', name: 'price', label: this.sharedSvc.translate(TranslationKey.price), category: category,
            schema: { type: 'number' }, editable: true, builtin: true, visible_for_planners: true,
            mandatory_for_planners: true,
        });
        result.push({
            id: 'currency', name: 'currency', label: this.sharedSvc.translate(TranslationKey.currency), category: category,
            schema: { type: 'string', enum: this.sharedSvc.getAllSupportedCurrencies() }, editable: true, builtin: true,
            visible_for_planners: true
        });
        result.push({
            id: 'min_per_buyer', name: 'min_per_buyer', label: this.sharedSvc.translate(TRANSLATION_MAP.MIN_PER_BUYER), category: category,
            schema: { type: 'integer', default: 0 }, editable: true, builtin: true, visible_for_planners: true
        });
        result.push({
            id: 'more_than_one', name: 'more_than_one',
            label: this.sharedSvc.translate(TranslationKey.allowReservingOrBuyingmoreThanOneProduct),
            category: category,
            schema: { type: 'boolean' }, editable: true, builtin: true, visible_for_planners: true
        });
        result.push({
            id: 'max_per_buyer', name: 'max_per_buyer', label: this.sharedSvc.translate(TranslationKey.maxPerBuyer), category: category,
            schema: { type: 'integer' }, editable: true, builtin: true, visible_for_planners: true
        });
        result.push(this.sharedSvc.createSelectField({
            label: this.sharedSvc.translate(TRANSLATION_MAP.TAX),
            id: 'tax_id', name: 'tax_id', category, editable: true, builtin: true, visible_for_planners: true,
            mandatory_for_planners: true,
        }, (taxes ?? []).reduce((record, tax) => {
            record[tax.id] = tax.name;
            return record;
        }, {} as Record<string, string>)));
        result.push(this.sharedSvc.createSelectField({
            label: this.sharedSvc.translate(TRANSLATION_MAP.CANCELLATION_TERMS),
            id: 'cancellation_terms_id', name: 'cancellation_terms_id', category, editable: true, builtin: true, visible_for_planners: true,
            mandatory_for_planners: true,
        }, (cancellationTerms ?? []).reduce((record, term) => {
            record[term.id] = term.name;
            return record;
        }, {} as Record<string, string>)));
        if (module === ProductModuleType.event) {
            result.push({
                id: 'max_units', name: 'max_units', label: this.sharedSvc.translate(TRANSLATION_MAP.MAX_UNITS),
                category: category,
                schema: { type: 'integer', exclusiveMinimum: 0,},
                editable: true, builtin: true,
                visible_for_planners: true,
            });
            result.push({
                id: 'start_selling_at', name: 'start_selling_at', label: this.sharedSvc.translate(TRANSLATION_MAP.START_SELLING_AT),
                category: category, schema: { type: ['string', 'null'], format: 'date-time' }, editable: true, builtin: true, type: 'datetime',
                visible_for_planners: true, timezone
            });
            result.push({
                id: 'stop_selling_at', name: 'stop_selling_at', label: this.sharedSvc.translate(TRANSLATION_MAP.STOP_SELLING_AT),
                category: category, schema: { type: ['string', 'null'], format: 'date-time' }, editable: true, builtin: true, type: 'datetime',
                visible_for_planners: true, timezone,
            });
            if (dependencies) {
                result.push(this.sharedSvc.createSelectField({
                    label: this.sharedSvc.translate(TRANSLATION_MAP.DEPENDENCY),
                    category,
                    name: 'dependency_id', id: 'dependency_id',
                }, dependencies.reduce((record, dependency) => {
                    record[dependency.id] = dependency.name;
                    return record;
                }, {} as Record<string, string>)));
            }
        }
        return result;
    }

    async getProductColumnsSettings(product: SettingEntityType, columns: IField[]): Promise<IColumnsListSetting> {
        const allowedIds = columns.map(x => x.id);
        const defaultNames = ['name', 'category', 'type', 'price', 'currency', 'more_than_one', 'max_per_buyer'];
        const defaultIds = columns.filter(x => defaultNames.indexOf(x.name) >= 0).map(x => x.id);
        const columnsSettings = await this.settingsSvc.getGlobalColumnsSettings(product, allowedIds, defaultIds);
        return columnsSettings;
    }

    getAdvancedSearchData(columns: IField[]): IAdvancedSearchData {
        const result: IAdvancedSearchData = {
            advancedFilters: [],
            allFields: columns
        };
        return result;
    }

    getProductActions(): IRowAction[] {
        const result: IRowAction[] = [
            {
                label: this.sharedSvc.translate(TranslationKey.viewDetailsMenuAction),
                id: ProductsRowActionId.viewDetails, icon: IconName.visibility, singleSelectionOnly: true,
                requiredScopes: this.rsSvc.getRequiredGetProductScopes()
            } as IRowAction,
            {
                label: this.sharedSvc.translate(TranslationKey.assignToProductGroups),
                id: ProductsRowActionId.assignToProductGroups, icon: IconName.category, singleSelectionOnly: false,
                requiredScopes: this.rsSvc.getRequiredGetProductGroupScopes()
            } as IRowAction,
            {
                label: this.sharedSvc.translate(TranslationKey.archiveMenuAction),
                id: ProductsRowActionId.delete, icon: IconName.delete, singleSelectionOnly: false,
                requiredScopes: this.rsSvc.getRequiredDeleteProductScopes()
            } as IRowAction,
        ];
        return result;
    }

    async showDeleteProductConfirmation(selectedItemsData?: IAzavistaSelectedItemsCountData): Promise<IConfirmationDialogComponentData> {
        return this.sharedSvc.showConfirmationDialog(
            this.sharedSvc.translate(TranslationKey.archiveProductDialogTitle),
            [
                this.sharedSvc.translate(TranslationKey.archiveProductDialogAreYouSure),
                // this.sharedSvc.translate(TranslationKey.wontBeAbleToRestoreData)
            ],
            false,
            selectedItemsData
        );
    }

    createNewProductGroupData(allProductFields: IField[], product?: IProduct): IAzavistaGroupedFieldsComponentData {
        const createNewProductFields = this.getCreateNewProductFields(allProductFields);
        const entity = product || {} as IProduct;
        const result: IAzavistaGroupedFieldsComponentData = {
            expandPanels: true,
            editModeForAllFields: true,
            singleColumn: true,
            showActionButtons: true,
            fieldsWithEntity: {
                entity: entity,
                fields: createNewProductFields
            }
        };
        return result;
    }

    getCreateNewProductFields(allProductFields: IField[]): IField[] {
        return allProductFields.filter(x => x.id === 'name');
    }

    reCreateSharedData() {
        this.sharedData = this.getSharedData();
    }

    private getSharedData() {
        return getAzavistaMemoryCache(this.apiSvc, [
            'getAllCancellationTerms',
            'getAllTaxes',
            'getAllDependencies',
        ]);
    }
}
